/* eslint-disable react-refresh/only-export-components -- provider + its hooks live together on purpose */
/**
 * React glue for the HL data layer.
 *
 * <HlProvider> (inside <EnvProvider> and <QueryClientProvider>) owns one InfoClient and the sockets (wsSockets.ts) for
 * the current environment. Switching env closes the old sockets, builds new clients and drops every
 * TanStack Query entry that is not keyed to the new env (see hlQueryKey).
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEnv, type EnvName } from '../config/env';
import { canonicalJson } from './canonical';
import { createInfoClient, type InfoClient, type InfoClientOptions } from './info';
import type { Subscription, SubscriptionData } from './types';
import { ConnectBudget, type FrameMeta, type WsManager, type WsState } from './ws';
import {
  SOCKET_OF_ROLE,
  WsSockets,
  failedSocketBudget,
  newSocketBudget,
  type WsRole,
  type WsSocketsOptions,
} from './wsSockets';
import { TabSleep, wsCounters } from './tabSleep';
import type { HealthStatus } from './health';
import { healthMonitor, type HealthMonitor } from './healthMonitor';

interface HlContextValue {
  env: EnvName;
  info: InfoClient;
  sockets: WsSockets;
  health: HealthMonitor;
}

const HlContext = createContext<HlContextValue | null>(null);

/**
 * Query keys for HL data must start with ['hl', envName, …] so an env switch never serves the other
 * network's cache. Example: useQuery({ queryKey: hlQueryKey(env, 'outcomeMeta'), … }).
 */
export function hlQueryKey(env: EnvName, ...parts: unknown[]): unknown[] {
  return ['hl', env, ...parts];
}

export interface HlProviderProps {
  children: ReactNode;
  /** Test seams. */
  infoOptions?: InfoClientOptions;
  wsOptions?: WsSocketsOptions;
}

export function HlProvider({ children, infoOptions, wsOptions }: HlProviderProps) {
  const { env } = useEnv();
  const queryClient = useQueryClient();
  // Options are test seams; read them once per env so a new object identity does not churn clients.
  const optsRef = useRef({ infoOptions, wsOptions });
  // #1809: one allowance of new sockets for the tab, shared by its sockets and kept across env switches. The backend's
  // managers have no cap.
  const [connectBudget] = useState(() => {
    const table = wsOptions?.socketOfRole ?? SOCKET_OF_ROLE;
    return new ConnectBudget(newSocketBudget(table), undefined, failedSocketBudget(table));
  });

  const value = useMemo<HlContextValue>(() => {
    // #771: every /info attempt feeds this network's health monitor (outage banner, ticket gate, status dot).
    const health = healthMonitor(env.name);
    const io = optsRef.current.infoOptions;
    const baseFetch = io?.fetch ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a));
    return {
      env: env.name,
      // #1809: the cooldown sits outside wrapFetch, so time spent waiting is not an /info attempt (no info_timeout).
      info: createInfoClient(env, { cooldown: health.cooldown, ...io, fetch: health.wrapFetch(baseFetch) }),
      sockets: new WsSockets(env.wsUrl, { connectBudget, counters: wsCounters, ...optsRef.current.wsOptions }),
      health,
    };
  }, [env, connectBudget]);

  // Tear down the sockets when the env changes or the provider unmounts. close() leaves them
  // reusable, so React StrictMode's mount/unmount/mount cycle simply reconnects lazily.
  useEffect(() => () => value.sockets.close(), [value]);

  // #1809 W12: a tab hidden for long holds no socket.
  useEffect(() => new TabSleep(value.sockets, value.health).start(), [value]);

  // Health monitor of the selected network: starts fresh (ok) on selection, follows the sockets, ticks while mounted.
  useEffect(() => {
    value.health.reset();
    const detach = value.health.attachWs(value.sockets);
    const stop = value.health.start();
    return () => {
      stop();
      detach();
    };
  }, [value]);

  // Reset the query cache on env change (skip the first mount).
  const prevEnv = useRef(env.name);
  useEffect(() => {
    if (prevEnv.current === env.name) return;
    prevEnv.current = env.name;
    const keep = env.name;
    const stale = (q: { queryKey: readonly unknown[] }) => !(q.queryKey[0] === 'hl' && q.queryKey[1] === keep);
    void queryClient.cancelQueries({ predicate: stale });
    queryClient.removeQueries({ predicate: stale });
  }, [env.name, queryClient]);

  return <HlContext.Provider value={value}>{children}</HlContext.Provider>;
}

function useHl(): HlContextValue {
  const ctx = useContext(HlContext);
  if (!ctx) throw new Error('HL hooks must be used inside <HlProvider>');
  return ctx;
}

export function useInfoClient(): InfoClient {
  return useHl().info;
}

/** The socket that carries a role. */
export function useWs(role: WsRole): WsManager {
  return useHl().sockets.forRole(role);
}

/** Environment the current HL clients point at (use it in query keys). */
export function useHlEnv(): EnvName {
  return useHl().env;
}

/** State of the socket carrying `role`, or with no role the worst of the sockets in use. */
export function useWsStatus(role?: WsRole): WsState {
  const { sockets } = useHl();
  const source = role ? sockets.forRole(role) : sockets;
  return useSyncExternalStore(
    (cb) => source.onStateChange(cb),
    () => source.state,
    () => source.state,
  );
}

/** Health of the selected Hyperliquid network as shown to the user (#771, #1809): 'ok' | 'degraded' | 'down'. */
export function useHlHealth(): HealthStatus {
  const { health } = useHl();
  return useSyncExternalStore(
    (cb) => health.subscribe(cb),
    () => health.visibleStatus,
    () => health.visibleStatus,
  );
}

/**
 * Subscribe to an HL WS stream for the component's lifetime, on the socket of its role. `sub` may be a fresh object
 * each render: identity is its canonical JSON. Pass null to stay unsubscribed (e.g. no wallet yet).
 * The handler can change freely without resubscribing. A handler mounted after another component already holds
 * the same l2Book or allMids is given its last frame first (`meta.replay`, #706).
 */
export function useSubscription<S extends Subscription>(
  sub: S | null | undefined,
  handler: (data: SubscriptionData<S>, sub: S, meta: FrameMeta) => void,
): void {
  const { sockets } = useHl();
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  const key = sub ? canonicalJson(sub) : null;
  useEffect(() => {
    if (!key) return;
    const parsed = JSON.parse(key) as S;
    return sockets.forSub(parsed).subscribe(parsed, (data, s, meta) => handlerRef.current(data, s, meta));
  }, [sockets, key]);
}

/** Run `cb` when a sleeping tab comes back (tabSleep.ts), with the time it was hidden, for what no reconnect hook re-syncs. */
export function useOnTabWake(cb: (hiddenSince: number) => void): void {
  const { sockets } = useHl();
  const ref = useRef(cb);
  useEffect(() => {
    ref.current = cb;
  });
  useEffect(() => sockets.onWake((since) => ref.current(since)), [sockets]);
}

/** Run `cb` after every reconnect of the socket carrying `role` (to re-snapshot its streams over REST). */
export function useOnWsReconnect(role: WsRole, cb: () => void): void {
  const ws = useWs(role);
  const ref = useRef(cb);
  useEffect(() => {
    ref.current = cb;
  });
  useEffect(() => ws.onReconnect(() => ref.current()), [ws]);
}
